/**
 * Create App Bundle - Universal Bundle Factory
 *
 * FULLY GENERIC - Apps don't even need to specify name!
 * Automatically handles common providers (QueryClient, ErrorBoundary)
 *
 * Usage:
 * ```
 * import { createAppBundle } from '@room/shared-ui';
 * import { App } from './App';
 * export default createAppBundle(App);
 * ```
 *
 * Or with custom wrapper:
 * ```
 * import { createAppBundle } from '@room/shared-ui';
 * import { App } from './App';
 * import { AppWrapper } from './AppWrapper';
 * export default createAppBundle(App, AppWrapper);
 * ```
 */

import { StrictMode, ComponentType, ReactNode } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppErrorBoundary } from '../components/AppErrorBoundary';
import { registerDefaultTiles } from '../stores/defaultTiles';
import { setAuthAPI, getAuthAPI } from '../stores/session';

// Shared query client for all bundles
const queryClient = new QueryClient();

// Track if global setup has been done
let globalSetupDone = false;

/**
 * Setup global state once (tiles, etc.)
 * Auth API is inherited from parent page (home screen)
 * If not available, setup a basic one
 */
function ensureGlobalSetup() {
  if (globalSetupDone) return;

  // Register default tiles (idempotent - safe to call multiple times)
  registerDefaultTiles();

  // Check if Auth API is already configured by parent page
  if (!getAuthAPI()) {
    console.log('[Bundle] Parent page has not setup Auth API, setting up bundle Auth API...');

    // Setup Auth API for bundle mode
    // Use fetch directly to avoid circular dependency with frontend-sdk
    const getSessionToken = () => {
      const localToken =
        localStorage.getItem('session_token') ?? localStorage.getItem('pi_session_token');
      if (localToken) {
        return localToken;
      }

      return (
        document.cookie
          .split('; ')
          .find(row => row.startsWith('session_token='))
          ?.split('=')[1] || null
      );
    };

    setAuthAPI({
      getSession: async () => {
        const token = getSessionToken();
        const response = await fetch('/api/auth/session', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        });

        if (!response.ok) {
          return { success: false, error: 'Failed to fetch session' };
        }

        const data = await response.json();
        return data;
      },
    });
  } else {
    console.log('[Bundle] Auth API already configured by parent page');
  }

  globalSetupDone = true;
  console.log('[Bundle] Global setup done');
}

/**
 * Extract app name from build-time environment or bundle name
 * This is set by vite.config.bundle.ts via define
 */
function getAppName(): string {
  // Try to get from build-time constant
  if (typeof (window as any).__ROOM_APP_NAME !== 'undefined') {
    return (window as any).__ROOM_APP_NAME;
  }

  // Fallback: extract from bundle script tag
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    const src = scripts[i].src;
    // Match patterns like: room-chat.pages.dev/bundle/app.js
    const match = src.match(/room-(\w+)\.pages\.dev\/bundle/);
    if (match) {
      return match[1].charAt(0).toUpperCase() + match[1].slice(1);
    }
  }

  // Default fallback
  return 'App';
}

/**
 * Creates a bundle interface for an app
 * Automatically wraps with QueryClient + ErrorBoundary if no custom wrapper provided
 *
 * @param AppComponent - Root component to render
 * @param WrapperComponent - Optional custom wrapper (if provided, you handle all providers)
 */
export function createAppBundle(
  AppComponent: ComponentType,
  WrapperComponent?: ComponentType<{ children: ReactNode }>
) {
  const appName = getAppName();
  let root: Root | null = null;

  const bundle = {
    mount: (containerId: string) => {
      const container = document.getElementById(containerId);
      if (!container) {
        console.error(`[${appName} Bundle] Container "${containerId}" not found`);
        return;
      }

      if (root) {
        root.unmount();
      }

      // Mark that we're running in bundle mode (apps can check this to disable redirects)
      (window as any).__ROOM_BUNDLE_MODE__ = true;

      // Ensure global setup (tiles, etc.) - runs once
      ensureGlobalSetup();

      console.log(`[${appName} Bundle] Creating React root for container:`, container);
      root = createRoot(container);
      console.log(`[${appName} Bundle] React root created`);

      // If custom wrapper provided, use it (assumes wrapper handles all providers)
      // Otherwise, auto-wrap with QueryClient + ErrorBoundary
      const Content = WrapperComponent
        ? () => {
            console.log(`[${appName} Bundle] Content rendering with custom wrapper`);
            return (
              <WrapperComponent>
                <AppComponent />
              </WrapperComponent>
            );
          }
        : () => {
            console.log(`[${appName} Bundle] Content rendering with QueryClient`);
            return (
              <AppErrorBoundary>
                <QueryClientProvider client={queryClient}>
                  <AppComponent />
                </QueryClientProvider>
              </AppErrorBoundary>
            );
          };

      console.log(`[${appName} Bundle] Calling root.render()...`);
      root.render(
        <StrictMode>
          <Content />
        </StrictMode>
      );
      console.log(`[${appName} Bundle] root.render() completed`);

      // Force a DOM check
      setTimeout(() => {
        console.log(`[${appName} Bundle] Container after render:`, {
          children: container.children.length,
          innerHTML: container.innerHTML.substring(0, 200),
        });
      }, 100);

      console.log(`[${appName} Bundle] Mounted successfully`);
    },

    unmount: () => {
      if (root) {
        root.unmount();
        root = null;
        // Clear bundle mode flag
        (window as any).__ROOM_BUNDLE_MODE__ = false;
        console.log(`[${appName} Bundle] Unmounted`);
      }
    },
  };

  // Register with GENERIC namespace - always use RoomAppBundle
  (window as any).RoomAppBundle = bundle;

  // Also register with app-specific name for backward compatibility
  const specificName = `Room${appName}Bundle`;
  (window as any)[specificName] = bundle;

  console.log(`[Bundle Factory] Registered as RoomAppBundle and ${specificName}`);

  return bundle;
}

export default createAppBundle;
